import React, { useEffect, useMemo, useState } from 'react'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Flex, Tabs as PillTab, TabsContainer } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { OtherText, TitleText } from 'styling/Typography'
import { Input } from 'components/Input/Input'
import { CheckChip } from 'components/CheckBox/CheckChip'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { IConfigurationTabProps } from 'types/foodLogistics/foodTabProps.interface'
import { useHasPermission } from 'hooks/useHasPermission'
import {
  archiveFoodDietaryTag,
  archiveFoodKitchen,
  createFoodCatchmentRule,
  createFoodDietaryTag,
  createFoodKitchen,
  createFoodMealWindow,
  deleteFoodCatchmentRule,
  deleteFoodKitchenStandbyConfig,
  deleteFoodMealWindow,
  getFoodCatchmentRules,
  getFoodConfigVersions,
  getFoodDietaryTags,
  getFoodKitchens,
  getFoodKitchenStandbyConfigs,
  getFoodMealWindows,
  updateFoodCatchmentRule,
  updateFoodDietaryTag,
  updateFoodKitchen,
  updateFoodMealWindow,
  upsertFoodKitchenStandbyConfig,
  upsertFoodSitePolicy,
} from 'features/food/foodSlice'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestTimePickerInput } from 'components/HonestTimePicker/HonestTimePickerInput'
import { ISelectValue } from 'constants/globalTypes'
import {
  FoodConfigUnknownPresencePolicy,
  FoodKitchenSupplyMode,
  FoodProvisioningMode,
} from 'types/api/foodApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import {
  FOOD_PROVISIONING_MODE_OPTIONS,
  FOOD_UNKNOWN_PRESENCE_OPTIONS,
  foodProvisioningModeLabel,
} from '../foodPlanningData'
import { ConfigFormGrid } from '../Food.styled'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import { toast } from 'react-toastify'

const CONFIG_SUB_TABS = [
  'Dietary Tags',
  'Kitchens',
  'Meal Windows',
  'Catchment Rules',
  'Site Policy',
  'Config Versions',
]

const operatorOptions = [
  { label: 'Internal', value: 1 },
  { label: 'Contractor', value: 2 },
]

const supplyModeOptions = [
  { label: 'Client supplied', value: FoodKitchenSupplyMode.ClientSupplied },
  { label: 'Contractor supplied', value: FoodKitchenSupplyMode.ContractorSupplied },
]

const parseFoodKitchens = (data: any) =>
  (data?.data || []).map((k: any) => ({ label: k.name, value: k.id }))

const parseDepartments = (data: any) =>
  (data?.data || []).map((item: any) => ({ label: item.name, value: item.id }))

const hhmmToMinutes = (value: string) => {
  const [hours, minutes] = String(value || '00:00').split(':')
  return Number(hours || 0) * 60 + Number(minutes || 0)
}

export const ConfigurationTab = ({ onToolbarChange }: IConfigurationTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const canKitchen = useHasPermission('kitchen')
  const {
    dietaryTags,
    logisticsKitchens,
    mealWindows,
    catchmentRules,
    configVersions,
    kitchenStandbyConfigs,
    isLoading,
  } = useSelector((store: RootState) => store.food)

  const visibleTabs = useMemo(
    () => CONFIG_SUB_TABS.filter((t) => t !== 'Kitchens' || canKitchen),
    [canKitchen],
  )

  const [tab, setTab] = useState<string>(CONFIG_SUB_TABS[0])

  useEffect(() => {
    if (!visibleTabs.includes(tab)) {
      setTab(visibleTabs[0] || CONFIG_SUB_TABS[0])
    }
  }, [visibleTabs, tab])

  const [dietaryCode, setDietaryCode] = useState('')
  const [dietaryName, setDietaryName] = useState('')
  const [dietaryEditCode, setDietaryEditCode] = useState<string | null>(null)

  const [kitchenId, setKitchenId] = useState<number | null>(null)
  const [kitchenName, setKitchenName] = useState('')
  const [kitchenOperatorType, setKitchenOperatorType] = useState(1)
  const [kitchenSupplyMode, setKitchenSupplyMode] = useState(FoodKitchenSupplyMode.ClientSupplied)
  const [kitchenStoreLocation, setKitchenStoreLocation] = useState<ISelectValue | null>(null)
  const [kitchenPackoutSupport, setKitchenPackoutSupport] = useState(true)
  const [kitchenStandbyEnabled, setKitchenStandbyEnabled] = useState(true)
  const [kitchenSoftCapacity, setKitchenSoftCapacity] = useState('0')
  const [kitchenTagCodes, setKitchenTagCodes] = useState<string[]>([])

  const [standbyKitchenId, setStandbyKitchenId] = useState<number | null>(null)
  const [standbySku, setStandbySku] = useState<ISelectValue | null>(null)
  const [standbyTagCodes, setStandbyTagCodes] = useState<string[]>([])
  const [standbyMinThreshold, setStandbyMinThreshold] = useState('0')
  const [standbyEnabled, setStandbyEnabled] = useState(true)

  const [mealWindowId, setMealWindowId] = useState<number | null>(null)
  const [mealWindowName, setMealWindowName] = useState('')
  const [mealWindowStart, setMealWindowStart] = useState('06:00')
  const [mealWindowEnd, setMealWindowEnd] = useState('08:00')
  const [mealWindowCutoffTime, setMealWindowCutoffTime] = useState('20:00')
  const [mealWindowOffset, setMealWindowOffset] = useState('-1')
  const [mealWindowKitchen, setMealWindowKitchen] = useState<ISelectValue | null>(null)

  const [catchmentId, setCatchmentId] = useState<number | null>(null)
  const [catchmentKitchen, setCatchmentKitchen] = useState<ISelectValue | null>(null)
  const [catchmentLocation, setCatchmentLocation] = useState<ISelectValue | null>(null)
  const [catchmentDepartment, setCatchmentDepartment] = useState<ISelectValue | null>(null)
  const [catchmentFallback, setCatchmentFallback] = useState(false)

  const [maxMealsPerDay, setMaxMealsPerDay] = useState('3')
  const [priorityWindowIds, setPriorityWindowIds] = useState<number[]>([])
  const [unknownPresencePolicy, setUnknownPresencePolicy] = useState(
    FoodConfigUnknownPresencePolicy.NoMeal,
  )
  const [defaultMode, setDefaultMode] = useState(FoodProvisioningMode.MessOrHall)
  const [noShowGraceMinutes, setNoShowGraceMinutes] = useState('30')
  const [cancelIntentOnNoShow, setCancelIntentOnNoShow] = useState(true)
  const [applyShowRateCoefficient, setApplyShowRateCoefficient] = useState(false)

  useEffect(() => {
    dispatch(getFoodDietaryTags({ page: 1, limit: 200 }))
    dispatch(getFoodMealWindows({ page: 1, limit: 200 }))
    dispatch(getFoodCatchmentRules({ page: 1, limit: 200 }))
    dispatch(getFoodConfigVersions({ page: 1, limit: 50 }))
    if (canKitchen) {
      dispatch(getFoodKitchens({ page: 1, limit: 200 }))
    }
  }, [dispatch, canKitchen])

  useEffect(() => {
    if (!(mealWindows.data || []).length) return
    if (priorityWindowIds.length) return
    setPriorityWindowIds((mealWindows.data || []).map((w) => w.id))
  }, [mealWindows.data, priorityWindowIds.length])

  useEffect(() => {
    onToolbarChange?.(
      <TabsContainer>
        {visibleTabs.map((t) => (
          <PillTab key={t} active={tab === t} onClick={() => setTab(t)}>
            {t}
          </PillTab>
        ))}
      </TabsContainer>,
    )
    return () => onToolbarChange?.(null)
  }, [tab, onToolbarChange, visibleTabs])

  const resetDietary = () => {
    setDietaryCode('')
    setDietaryName('')
    setDietaryEditCode(null)
  }

  const resetKitchen = () => {
    setKitchenId(null)
    setKitchenName('')
    setKitchenOperatorType(1)
    setKitchenSupplyMode(FoodKitchenSupplyMode.ClientSupplied)
    setKitchenStoreLocation(null)
    setKitchenPackoutSupport(true)
    setKitchenStandbyEnabled(true)
    setKitchenSoftCapacity('0')
    setKitchenTagCodes([])
  }

  const resetStandbyForm = () => {
    setStandbySku(null)
    setStandbyTagCodes([])
    setStandbyMinThreshold('0')
    setStandbyEnabled(true)
  }

  const resetMealWindow = () => {
    setMealWindowId(null)
    setMealWindowName('')
    setMealWindowStart('06:00')
    setMealWindowEnd('08:00')
    setMealWindowCutoffTime('20:00')
    setMealWindowOffset('-1')
    setMealWindowKitchen(null)
  }

  const resetCatchment = () => {
    setCatchmentId(null)
    setCatchmentKitchen(null)
    setCatchmentLocation(null)
    setCatchmentDepartment(null)
    setCatchmentFallback(false)
  }

  const submitDietary = async () => {
    if (!dietaryName.trim()) return
    if (dietaryEditCode) {
      await dispatch(updateFoodDietaryTag({ code: dietaryEditCode, body: { name: dietaryName.trim() } }))
    } else {
      if (!dietaryCode.trim()) return
      await dispatch(createFoodDietaryTag({ code: dietaryCode.trim(), name: dietaryName.trim() }))
    }
    resetDietary()
    dispatch(getFoodDietaryTags({ page: 1, limit: 200 }))
  }

  const submitKitchen = async () => {
    if (!kitchenName.trim()) return
    const storeLocationId = kitchenStoreLocation?.value ? Number(kitchenStoreLocation.value) : null
    if (kitchenSupplyMode === FoodKitchenSupplyMode.ClientSupplied && !storeLocationId) {
      toast.warn('Client-supplied kitchens require a store location')
      return
    }
    if (kitchenStandbyEnabled && !storeLocationId) {
      toast.warn('Store location must be set before standby can be enabled')
      return
    }
    const body = {
      name: kitchenName.trim(),
      operatorType: Number(kitchenOperatorType),
      supplyMode: Number(kitchenSupplyMode),
      storeLocationId,
      packoutSupport: kitchenPackoutSupport,
      softCapacity: Number(kitchenSoftCapacity),
      standbyEnabled: kitchenStandbyEnabled,
      dietaryTagCodes: kitchenTagCodes,
    }
    if (kitchenId) {
      await dispatch(updateFoodKitchen({ id: kitchenId, body }))
    } else {
      await dispatch(createFoodKitchen(body))
    }
    resetKitchen()
    dispatch(getFoodKitchens({ page: 1, limit: 200 }))
  }

  const submitStandby = async () => {
    if (!standbyKitchenId) return
    if (!standbySku?.value) {
      toast.warn('Standby dish SKU must be selected')
      return
    }
    if (!standbyTagCodes.length) {
      toast.warn('Select at least one dietary tag for the standby item')
      return
    }
    const resp = await dispatch(
      upsertFoodKitchenStandbyConfig({
        kitchenId: standbyKitchenId,
        body: {
          standbyDishSkuId: Number(standbySku.value),
          dietaryTagCodes: standbyTagCodes,
          minThreshold: Number(standbyMinThreshold) || 0,
          enabled: standbyEnabled,
        },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      resetStandbyForm()
    }
  }

  const submitMealWindow = async () => {
    if (!mealWindowName.trim()) return
    const body = {
      name: mealWindowName.trim(),
      serviceStart: mealWindowStart,
      serviceEnd: mealWindowEnd,
      cutoffTime: mealWindowCutoffTime,
      cutoffDayOffset: Number(mealWindowOffset),
      defaultKitchenId: mealWindowKitchen?.value ? Number(mealWindowKitchen.value) : null,
    }
    if (mealWindowId) {
      await dispatch(updateFoodMealWindow({ id: mealWindowId, body }))
    } else {
      await dispatch(createFoodMealWindow(body))
    }
    resetMealWindow()
    dispatch(getFoodMealWindows({ page: 1, limit: 200 }))
  }

  const submitCatchment = async () => {
    if (!catchmentKitchen?.value) {
      toast.warn('Kitchen must be selected')
      return
    }
    const body = {
      kitchenId: Number(catchmentKitchen.value),
      locationNodeId: catchmentLocation?.value ? Number(catchmentLocation.value) : null,
      departmentId: catchmentDepartment?.value ? Number(catchmentDepartment.value) : null,
      isFallback: catchmentFallback,
    }
    if (catchmentId) {
      await dispatch(updateFoodCatchmentRule({ id: catchmentId, body }))
    } else {
      await dispatch(createFoodCatchmentRule(body))
    }
    resetCatchment()
    dispatch(getFoodCatchmentRules({ page: 1, limit: 200 }))
  }

  const dietaryColumns: IHonestTableColumn<any>[] = [
    { id: 'code', header: 'Code', cell: (row) => row.code },
    { id: 'name', header: 'Name', cell: (row) => row.name },
    { id: 'createdAt', header: 'Created', cell: (row) => row.createdAt?.slice(0, 10) || '—' },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setDietaryEditCode(row.code)
              setDietaryCode(row.code)
              setDietaryName(row.name)
            }}
          >
            Edit
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(archiveFoodDietaryTag(row.code))}>
            Archive
          </Button>
        </Flex>
      ),
    },
  ]

  const kitchenColumns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'Kitchen', cell: (row) => row.name },
    { id: 'operatorType', header: 'Operator', cell: (row) => (row.operatorType === 2 ? 'Contractor' : 'Internal') },
    {
      id: 'supplyMode',
      header: 'Supply',
      cell: (row) =>
        row.supplyMode === FoodKitchenSupplyMode.ContractorSupplied
          ? 'Contractor supplied'
          : 'Client supplied',
    },
    { id: 'storeLocation', header: 'Store', cell: (row) => row.storeLocation?.name || '—' },
    { id: 'softCapacity', header: 'Soft cap', cell: (row) => row.softCapacity },
    {
      id: 'dietaryTagCapabilities',
      header: 'Tags',
      cell: (row) => (
        <Flex gap='0.25rem' wrap>
          {(row.dietaryTagCapabilities || []).map((tag: any) => (
            <StatusPill key={tag.code} status='accent'>{tag.code}</StatusPill>
          ))}
        </Flex>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setKitchenId(row.id)
              setKitchenName(row.name)
              setKitchenOperatorType(row.operatorType)
              setKitchenSupplyMode(row.supplyMode || FoodKitchenSupplyMode.ClientSupplied)
              setKitchenStoreLocation(
                row.storeLocation
                  ? { label: row.storeLocation.name, value: row.storeLocation.id }
                  : null,
              )
              setKitchenPackoutSupport(Boolean(row.packoutSupport))
              setKitchenStandbyEnabled(Boolean(row.standbyEnabled))
              setKitchenSoftCapacity(String(row.softCapacity))
              setKitchenTagCodes((row.dietaryTagCapabilities || []).map((tag: any) => tag.code))
            }}
          >
            Edit
          </Button>
          <Button
            size='sm'
            variant={standbyKitchenId === row.id ? 'primary' : 'secondary'}
            onClick={() => {
              const next = standbyKitchenId === row.id ? null : row.id
              setStandbyKitchenId(next)
              resetStandbyForm()
              if (next) dispatch(getFoodKitchenStandbyConfigs(next))
            }}
          >
            Standby
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(archiveFoodKitchen(row.id))}>
            Archive
          </Button>
        </Flex>
      ),
    },
  ]

  const standbyColumns: IHonestTableColumn<any>[] = [
    { id: 'standbyDishSku', header: 'Dish SKU', cell: (row) => row.standbyDishSku?.name || '—' },
    {
      id: 'dietaryTags',
      header: 'Tags',
      cell: (row) => (
        <Flex gap='0.25rem' wrap>
          {(row.dietaryTags || []).map((tag: any) => (
            <StatusPill key={tag.code} status='accent'>{tag.code}</StatusPill>
          ))}
        </Flex>
      ),
    },
    { id: 'minThreshold', header: 'Min threshold', cell: (row) => row.minThreshold },
    {
      id: 'quantityOnHand',
      header: 'On hand',
      cell: (row) => (
        <StatusPill status={row.belowThreshold ? 'danger' : 'success'}>
          {row.quantityOnHand}
        </StatusPill>
      ),
    },
    {
      id: 'enabled',
      header: 'Enabled',
      cell: (row) => (
        <StatusPill status={row.enabled ? 'info' : 'neutral'}>{row.enabled ? 'Yes' : 'No'}</StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setStandbySku({ label: row.standbyDishSku?.name, value: row.standbyDishSku?.id })
              setStandbyTagCodes((row.dietaryTags || []).map((tag: any) => tag.code))
              setStandbyMinThreshold(String(row.minThreshold))
              setStandbyEnabled(Boolean(row.enabled))
            }}
          >
            Edit
          </Button>
          <Button
            size='sm'
            variant='secondary'
            onClick={() =>
              dispatch(
                deleteFoodKitchenStandbyConfig({
                  kitchenId: standbyKitchenId,
                  standbyId: row.id,
                }),
              )
            }
          >
            Delete
          </Button>
        </Flex>
      ),
    },
  ]

  const mealWindowColumns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'Name', cell: (row) => row.name },
    { id: 'serviceStart', header: 'Start', cell: (row) => row.serviceStart },
    { id: 'serviceEnd', header: 'End', cell: (row) => row.serviceEnd },
    { id: 'cutoffTime', header: 'Cutoff', cell: (row) => `${row.cutoffTime} (${row.cutoffDayOffset})` },
    { id: 'defaultKitchen', header: 'Default Kitchen', cell: (row) => row.defaultKitchen?.name || '—' },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setMealWindowId(row.id)
              setMealWindowName(row.name)
              setMealWindowStart(row.serviceStart)
              setMealWindowEnd(row.serviceEnd)
              setMealWindowCutoffTime(row.cutoffTime)
              setMealWindowOffset(String(row.cutoffDayOffset))
              setMealWindowKitchen(
                row.defaultKitchen
                  ? { label: row.defaultKitchen.name, value: row.defaultKitchen.id }
                  : null,
              )
            }}
          >
            Edit
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(deleteFoodMealWindow(row.id))}>
            Delete
          </Button>
        </Flex>
      ),
    },
  ]

  const catchmentColumns: IHonestTableColumn<any>[] = [
    { id: 'kitchen', header: 'Kitchen', cell: (row) => row.kitchen?.name || '—' },
    {
      id: 'locationNode',
      header: 'Location',
      cell: (row) => row.locationNode?.name || '—',
    },
    {
      id: 'department',
      header: 'Department',
      cell: (row) => row.department?.name || '—',
    },
    {
      id: 'isFallback',
      header: 'Fallback',
      cell: (row) => <StatusPill status={row.isFallback ? 'info' : 'neutral'}>{row.isFallback ? 'Yes' : 'No'}</StatusPill>,
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setCatchmentId(row.id)
              setCatchmentKitchen(
                row.kitchen ? { label: row.kitchen.name, value: row.kitchen.id } : null,
              )
              setCatchmentLocation(
                row.locationNode
                  ? { label: row.locationNode.name, value: row.locationNode.id }
                  : null,
              )
              setCatchmentDepartment(
                row.department
                  ? { label: row.department.name, value: row.department.id }
                  : null,
              )
              setCatchmentFallback(Boolean(row.isFallback))
            }}
          >
            Edit
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(deleteFoodCatchmentRule(row.id))}>
            Delete
          </Button>
        </Flex>
      ),
    },
  ]

  const togglePriorityWindow = (id: number) => {
    setPriorityWindowIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const movePriorityWindow = (id: number, dir: -1 | 1) => {
    setPriorityWindowIds((prev) => {
      const idx = prev.indexOf(id)
      if (idx < 0) return prev
      const next = [...prev]
      const swap = idx + dir
      if (swap < 0 || swap >= next.length) return prev
      const tmp = next[idx]
      next[idx] = next[swap]
      next[swap] = tmp
      return next
    })
  }

  const submitSitePolicy = async () => {
    const max = Number(maxMealsPerDay)
    if (!max || max < 1) {
      toast.warn('Max meals per day must be at least 1')
      return
    }
    if (!priorityWindowIds.length) {
      toast.warn('Select at least one meal window for priority order')
      return
    }
    const resp = await dispatch(
      upsertFoodSitePolicy({
        maxMealsPerDay: max,
        mealSelectionPriorityWindowIds: priorityWindowIds,
        unknownPresencePolicy,
        defaultMode,
        attendanceReconciliation: {
          noShowGraceMinutes: Number(noShowGraceMinutes) || 0,
          cancelIntentOnNoShow,
          applyShowRateCoefficient,
        },
      }),
    )
    if (resp.meta.requestStatus === 'fulfilled') {
      dispatch(getFoodConfigVersions({ page: 1, limit: 50 }))
    }
  }

  const versionColumns: IHonestTableColumn<any>[] = [
    { id: 'id', header: 'Version', cell: (row) => `#${row.id}` },
    {
      id: 'effectiveFromOperationalDay',
      header: 'Effective from',
      cell: (row) => formatDateForDisplay(row.effectiveFromOperationalDay),
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (row) => formatDateForDisplay(row.createdAt, true),
    },
    { id: 'createdBy', header: 'Created by', cell: (row) => row.createdBy },
    {
      id: 'policy',
      header: 'Policy snapshot',
      minWidth: '16rem',
      cell: (row) => {
        const p = row.payload?.policy
        if (!p) return '—'
        return `${p.maxMealsPerDay} meals/day · ${foodProvisioningModeLabel(p.defaultMode)} · unknown=${p.unknownPresencePolicy}`
      },
    },
  ]

  return (
    <Flex column gap='1rem' width='100%'>
      {tab === 'Dietary Tags' && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Dietary Tags</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <ConfigFormGrid>
              <Input
                labelText='Code'
                placeholder='e.g. HALAL'
                value={dietaryCode}
                onChange={(e: any) => setDietaryCode(e.target.value)}
                disabled={Boolean(dietaryEditCode)}
              />
              <Input
                labelText='Name'
                placeholder='e.g. Halal'
                value={dietaryName}
                onChange={(e: any) => setDietaryName(e.target.value)}
              />
            </ConfigFormGrid>
            <Flex gap='0.5rem' alignEnd>
              <Button variant='primary' onClick={submitDietary}>{dietaryEditCode ? 'Update' : 'Create'}</Button>
              {dietaryEditCode && <Button variant='secondary' onClick={resetDietary}>Cancel</Button>}
            </Flex>
            <HonestTable columns={dietaryColumns} data={dietaryTags.data || []} keyField='code' loading={isLoading} />
          </ContainerContent>
        </Container>
      )}

      {tab === 'Kitchens' && canKitchen && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Kitchens</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <ConfigFormGrid>
              <Input
                labelText='Name'
                placeholder='e.g. Main Kitchen'
                value={kitchenName}
                onChange={(e: any) => setKitchenName(e.target.value)}
              />
              <Input
                labelText='Soft Capacity'
                type='number'
                placeholder='e.g. 120'
                value={kitchenSoftCapacity}
                onChange={(e: any) => setKitchenSoftCapacity(e.target.value)}
              />
              <HonestSelectInput
                nomarg
                width='100%'
                labelText='Operator Type'
                placeholder='Select operator…'
                options={operatorOptions}
                defaultValue={kitchenOperatorType}
                onChange={(v: any) => setKitchenOperatorType(Number(v.value))}
                name='operatorType'
              />
              <HonestSelectInput
                nomarg
                width='100%'
                labelText='Supply Mode'
                placeholder='Select supply mode…'
                options={supplyModeOptions}
                defaultValue={kitchenSupplyMode}
                onChange={(v: any) => setKitchenSupplyMode(Number(v.value))}
                name='supplyMode'
              />
              <HonestSelectAsyncInput
                nomarg
                width='100%'
                keyProp={kitchenId || 0}
                link='locations'
                name='storeLocation'
                labelText='Store Location'
                placeholder='Search location…'
                defaultValue={kitchenStoreLocation?.label || ''}
                onChange={(v: ISelectValue) => setKitchenStoreLocation(v?.value ? v : null)}
                parserFunction={parseLocationRegistryOptions}
              />
            </ConfigFormGrid>
            <OtherText grey>
              Client-supplied kitchens require a store location. Standby needs a store location too.
            </OtherText>
            <Flex gap='0.5rem' wrap>
              {(dietaryTags.data || []).map((tag: any) => (
                <CheckChip
                  key={tag.code}
                  text={tag.code}
                  active={kitchenTagCodes.includes(tag.code)}
                  onClick={() =>
                    setKitchenTagCodes((prev) =>
                      prev.includes(tag.code) ? prev.filter((x) => x !== tag.code) : [...prev, tag.code],
                    )
                  }
                />
              ))}
            </Flex>
            <Flex gap='0.5rem' alignEnd>
              <Button variant={kitchenPackoutSupport ? 'primary' : 'secondary'} onClick={() => setKitchenPackoutSupport((v) => !v)}>
                Packout: {kitchenPackoutSupport ? 'Yes' : 'No'}
              </Button>
              <Button variant={kitchenStandbyEnabled ? 'primary' : 'secondary'} onClick={() => setKitchenStandbyEnabled((v) => !v)}>
                Standby: {kitchenStandbyEnabled ? 'Yes' : 'No'}
              </Button>
              <Button variant='primary' onClick={submitKitchen}>{kitchenId ? 'Update' : 'Create'}</Button>
              {kitchenId && <Button variant='secondary' onClick={resetKitchen}>Cancel</Button>}
            </Flex>
            <HonestTable columns={kitchenColumns} data={logisticsKitchens.data || []} keyField='id' loading={isLoading} />
            {standbyKitchenId && (
              <Flex column gap='1rem' width='100%'>
                <TitleText weight='700'>
                  Standby —{' '}
                  {(logisticsKitchens.data || []).find((k) => k.id === standbyKitchenId)?.name ||
                    `Kitchen #${standbyKitchenId}`}
                </TitleText>
                <OtherText grey>
                  One standby item per dietary tag set, held as stock at the kitchen store. Standby
                  is deducted once at kitchen confirmation. Disabling removes the only path for late
                  arrivals, ad-hoc and emergency meals at this kitchen.
                </OtherText>
                <ConfigFormGrid>
                  <HonestSelectAsyncInput
                    link='inventory/item'
                    name='standbySku'
                    labelText='Standby dish SKU'
                    placeholder='Inventory item...'
                    defaultValue={standbySku?.label}
                    onChange={(v: ISelectValue) => setStandbySku(v)}
                    parserFunction={(data: any) =>
                      data.data.map((i: any) => ({ label: i.name, value: i.id }))
                    }
                    nomarg
                    width='100%'
                  />
                  <Input
                    labelText='Min threshold'
                    type='number'
                    placeholder='e.g. 5'
                    value={standbyMinThreshold}
                    onChange={(e: any) => setStandbyMinThreshold(e.target.value)}
                    nomarg
                  />
                </ConfigFormGrid>
                <Flex gap='0.5rem' wrap>
                  <Button
                    variant={standbyEnabled ? 'primary' : 'secondary'}
                    onClick={() => setStandbyEnabled((v) => !v)}
                  >
                    Enabled: {standbyEnabled ? 'Yes' : 'No'}
                  </Button>
                  <Button variant='primary' onClick={submitStandby}>
                    Save standby
                  </Button>
                </Flex>
                <Flex gap='0.5rem' wrap>
                  {(dietaryTags.data || []).map((tag: any) => (
                    <CheckChip
                      key={tag.code}
                      text={tag.code}
                      active={standbyTagCodes.includes(tag.code)}
                      onClick={() =>
                        setStandbyTagCodes((prev) =>
                          prev.includes(tag.code)
                            ? prev.filter((x) => x !== tag.code)
                            : [...prev, tag.code],
                        )
                      }
                    />
                  ))}
                </Flex>
                <HonestTable
                  columns={standbyColumns}
                  data={kitchenStandbyConfigs[standbyKitchenId] || []}
                  keyField='id'
                  emptyMessage='No standby configuration for this kitchen yet'
                />
              </Flex>
            )}
          </ContainerContent>
        </Container>
      )}

      {tab === 'Meal Windows' && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Meal Window Definitions</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <ConfigFormGrid>
              <Input
                labelText='Name'
                placeholder='e.g. Breakfast'
                value={mealWindowName}
                onChange={(e: any) => setMealWindowName(e.target.value)}
              />
              <HonestSelectAsyncInput
                nomarg
                width='100%'
                keyProp={mealWindowId || 0}
                link='food/management/kitchen'
                name='defaultKitchen'
                labelText='Default Kitchen'
                placeholder='Search kitchen…'
                defaultValue={mealWindowKitchen?.label || ''}
                onChange={(v: ISelectValue) => setMealWindowKitchen(v?.value ? v : null)}
                parserFunction={parseFoodKitchens}
              />
              <HonestTimePickerInput
                key={`mw-start-${mealWindowId || 'new'}`}
                nomarg
                width='100%'
                labelText='Service Start'
                defaultValue={hhmmToMinutes(mealWindowStart)}
                onChange={(time) => setMealWindowStart(time)}
              />
              <HonestTimePickerInput
                key={`mw-end-${mealWindowId || 'new'}`}
                nomarg
                width='100%'
                labelText='Service End'
                defaultValue={hhmmToMinutes(mealWindowEnd)}
                onChange={(time) => setMealWindowEnd(time)}
              />
              <HonestTimePickerInput
                key={`mw-cutoff-${mealWindowId || 'new'}`}
                nomarg
                width='100%'
                labelText='Cutoff Time'
                defaultValue={hhmmToMinutes(mealWindowCutoffTime)}
                onChange={(time) => setMealWindowCutoffTime(time)}
              />
              <Input
                labelText='Cutoff Day Offset'
                type='number'
                placeholder='0 or -1'
                value={mealWindowOffset}
                onChange={(e: any) => setMealWindowOffset(e.target.value)}
              />
            </ConfigFormGrid>
            <Flex gap='0.5rem'>
              <Button variant='primary' onClick={submitMealWindow}>{mealWindowId ? 'Update' : 'Create'}</Button>
              {mealWindowId && <Button variant='secondary' onClick={resetMealWindow}>Cancel</Button>}
            </Flex>
            <HonestTable columns={mealWindowColumns} data={mealWindows.data || []} keyField='id' loading={isLoading} />
          </ContainerContent>
        </Container>
      )}

      {tab === 'Catchment Rules' && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Catchment Rules</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <ConfigFormGrid>
              <HonestSelectAsyncInput
                nomarg
                width='100%'
                keyProp={(catchmentId || 0) + 1}
                link='food/management/kitchen'
                name='catchmentKitchen'
                labelText='Kitchen'
                placeholder='Search kitchen…'
                defaultValue={catchmentKitchen?.label || ''}
                onChange={(v: ISelectValue) => setCatchmentKitchen(v?.value ? v : null)}
                parserFunction={parseFoodKitchens}
              />
              <HonestSelectAsyncInput
                nomarg
                width='100%'
                keyProp={(catchmentId || 0) + 2}
                link='locations'
                name='catchmentLocation'
                labelText='Location'
                placeholder='Search location…'
                defaultValue={catchmentLocation?.label || ''}
                onChange={(v: ISelectValue) => setCatchmentLocation(v?.value ? v : null)}
                parserFunction={parseLocationRegistryOptions}
              />
              <HonestSelectAsyncInput
                nomarg
                width='100%'
                keyProp={(catchmentId || 0) + 3}
                link='departments'
                name='catchmentDepartment'
                labelText='Department'
                placeholder='Search department…'
                defaultValue={catchmentDepartment?.label || ''}
                onChange={(v: ISelectValue) => setCatchmentDepartment(v?.value ? v : null)}
                parserFunction={parseDepartments}
              />
              <Flex alignEnd>
                <Button variant={catchmentFallback ? 'primary' : 'secondary'} onClick={() => setCatchmentFallback((v) => !v)}>
                  Fallback: {catchmentFallback ? 'Yes' : 'No'}
                </Button>
              </Flex>
            </ConfigFormGrid>
            <OtherText grey>When department is set, backend forces fallback to true.</OtherText>
            <Flex gap='0.5rem'>
              <Button variant='primary' onClick={submitCatchment}>{catchmentId ? 'Update' : 'Create'}</Button>
              {catchmentId && <Button variant='secondary' onClick={resetCatchment}>Cancel</Button>}
            </Flex>
            <HonestTable columns={catchmentColumns} data={catchmentRules.data || []} keyField='id' loading={isLoading} />
          </ContainerContent>
        </Container>
      )}

      {tab === 'Site Policy' && (
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Site Policy</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Saves stamp a new configuration version effective the next operational day.
            </OtherText>
            <ConfigFormGrid>
              <Input
                labelText='Max meals per day'
                type='number'
                placeholder='e.g. 3'
                value={maxMealsPerDay}
                onChange={(e: any) => setMaxMealsPerDay(e.target.value)}
              />
              <Input
                labelText='No-show grace (minutes)'
                type='number'
                placeholder='e.g. 30'
                value={noShowGraceMinutes}
                onChange={(e: any) => setNoShowGraceMinutes(e.target.value)}
              />
              <HonestSelectInput
                nomarg
                width='100%'
                labelText='Unknown presence policy'
                placeholder='Select policy…'
                options={FOOD_UNKNOWN_PRESENCE_OPTIONS}
                defaultValue={unknownPresencePolicy}
                onChange={(v: any) => setUnknownPresencePolicy(Number(v.value))}
                name='unknownPresence'
              />
              <HonestSelectInput
                nomarg
                width='100%'
                labelText='Default provisioning mode'
                placeholder='Select mode…'
                options={FOOD_PROVISIONING_MODE_OPTIONS}
                defaultValue={defaultMode}
                onChange={(v: any) => setDefaultMode(Number(v.value))}
                name='defaultMode'
              />
            </ConfigFormGrid>
            <Flex gap='0.5rem' wrap>
              <Button
                variant={cancelIntentOnNoShow ? 'primary' : 'secondary'}
                onClick={() => setCancelIntentOnNoShow((v) => !v)}
              >
                Cancel intent on no-show: {cancelIntentOnNoShow ? 'Yes' : 'No'}
              </Button>
              <Button
                variant={applyShowRateCoefficient ? 'primary' : 'secondary'}
                onClick={() => setApplyShowRateCoefficient((v) => !v)}
              >
                Apply show-rate coefficient: {applyShowRateCoefficient ? 'Yes' : 'No'}
              </Button>
            </Flex>
            <OtherText grey>MEAL SELECTION PRIORITY (order matters)</OtherText>
            <Flex column gap='0.5rem'>
              {(mealWindows.data || []).map((w) => {
                const active = priorityWindowIds.includes(w.id)
                const order = priorityWindowIds.indexOf(w.id)
                return (
                  <Flex key={w.id} between center gap='0.5rem' width='100%'>
                    <Button
                      variant={active ? 'primary' : 'secondary'}
                      size='sm'
                      onClick={() => togglePriorityWindow(w.id)}
                    >
                      {active ? `#${order + 1} ${w.name}` : w.name}
                    </Button>
                    {active && (
                      <Flex gap='0.25rem'>
                        <Button size='sm' variant='secondary' onClick={() => movePriorityWindow(w.id, -1)}>
                          Up
                        </Button>
                        <Button size='sm' variant='secondary' onClick={() => movePriorityWindow(w.id, 1)}>
                          Down
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                )
              })}
            </Flex>
            <Button variant='primary' onClick={submitSitePolicy}>
              Save site policy
            </Button>
          </ContainerContent>
        </Container>
      )}

      {tab === 'Config Versions' && (
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Configuration Versions</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Immutable snapshots stamped automatically when kitchens, meal windows, dietary tags,
              catchment rules or site policy change. Newest first.
            </OtherText>
            <HonestTable
              columns={versionColumns}
              data={configVersions.data || []}
              keyField='id'
              loading={isLoading}
            />
          </ContainerContent>
        </Container>
      )}
    </Flex>
  )
}
